<!-- sections/DateRangeSection.vue -->
<script setup lang="ts">
import { computed } from 'vue';
import BaseSection from '../components/BaseSection.vue';
import ConnxField from '@/components/Molecules/Field/ConnxField.vue';
import { InputType } from '@/interfaces/InputFields';
import { numericMinMax } from '@/validation/numeric-min-max';
import { useDateRangeSettings } from '../composables/useDateRangeSettings';

interface Props {
  previousMonths?: {
    labelPrefix: string;
    labelAffix: string;
    value: number | null;
    min?: number;
    max?: number;
  };
  previousRange?: {
    selected: boolean;
    value: [string, string];
    type: string;
  };
  errors?: Map<string, string | null>;
  showTitle?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  errors: () => new Map(),
  showTitle: false,
});

const emit = defineEmits<{
  'field-error': [{ name: string; error: string | null }];
  'update:previous-range-selected': [selected: boolean];
  'update:previous-months': [value: number | null];
  'update:previous-range-from': [value: string];
  'update:previous-range-to': [value: string];
}>();

const {
  previousMonths,
  withinXMonthsMax,
  previousRangeFrom,
  previousRangeTo,
  previousRangeMin,
  previousRangeMax,
  previousRangeFromTouched,
  reportingPeriodChoice,
  fromDateRules,
  toDateRules,
  handleFieldError,
} = useDateRangeSettings(props, emit);

const shouldShow = computed(() => {
  return props.previousMonths !== undefined || props.previousRange !== undefined;
});
</script>

<template>
  <BaseSection title="Reporting Period" :should-show="shouldShow" :show-title="showTitle">
    <!-- Reporting Period -->
    <div v-if="props.previousMonths !== undefined || props.previousRange !== undefined">
      <div v-if="props.previousMonths && !props.previousRange" class="tw-flex tw-items-center tw-gap-2 tw-mb-2">
        <label class="Field__label tw-mb-0" for="previousMonths">
          {{ props.previousMonths?.labelPrefix }}
        </label>
        <ConnxField
          name="previousMonths"
          v-model="previousMonths"
          class="tw-w-52"
          :rules="numericMinMax(1, props.previousMonths?.max)"
          required
          :type="InputType.Number"
          @update:error="handleFieldError"
        />
        <label class="Field__label tw-mb-0" for="previousMonths">
          {{ props.previousMonths?.labelAffix }}
        </label>
      </div>

      <ConnxField
        v-else-if="props.previousMonths !== undefined && props.previousRange !== undefined"
        name="reporting-period-choice"
        label="Reporting Period"
        v-model="reportingPeriodChoice"
        class="tw-mb-0 tw-pt-0"
        :type="InputType.Radio"
        :options="[
          { label: 'Previous Period', value: 'previousMonths' },
          { label: 'Custom Range', value: 'previousRange' },
        ]"
      >
        <template #previousMonths>
          <div
            class="tw-flex tw-items-start tw-gap-2 tw-ml-10 tw-mb-2"
            v-if="reportingPeriodChoice === 'previousMonths'"
          >
            <label class="Field__label tw-mt-3" for="previousMonths">
              {{ props.previousMonths?.labelPrefix }}
            </label>
            <ConnxField
              name="previousMonths"
              v-model="previousMonths"
              class="tw-w-52"
              :rules="numericMinMax(1, props.previousMonths?.max)"
              required
              :type="InputType.Number"
              @update:error="handleFieldError"
            />
            <label class="Field__label tw-mt-3" for="previousMonths">
              {{ props.previousMonths?.labelAffix }}
            </label>
          </div>
        </template>

        <template #previousRange>
          <div class="tw-flex tw-flex-col tw-gap-4 tw-ml-10" v-if="reportingPeriodChoice === 'previousRange'">
            <div class="tw-flex tw-items-start tw-gap-x-4">
              <ConnxField
                name="previousRangeFrom"
                v-model="previousRangeFrom"
                :type="InputType.Date"
                :pickerType="props.previousRange?.type"
                :rules="fromDateRules"
                v-bind="previousRangeMax ? { 'max-date': previousRangeMax } : {}"
                label="From"
                label-inline
                @update:error="handleFieldError"
              />
              <ConnxField
                name="previousRangeTo"
                v-model="previousRangeTo"
                :disabled="previousRangeFrom === null && !previousRangeFromTouched"
                :type="InputType.Date"
                :pickerType="props.previousRange?.type"
                label="To"
                v-bind="previousRangeMin ? { 'min-date': previousRangeMin } : {}"
                label-inline
                :rules="toDateRules"
                @update:error="handleFieldError"
              />
            </div>
          </div>
        </template>
      </ConnxField>
    </div>
  </BaseSection>
</template>
